import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { MessageSquarePlus, Bug, Lightbulb, HelpCircle, Heart, Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "bug", label: "Bug", icon: Bug },
  { id: "idea", label: "Idea", icon: Lightbulb },
  { id: "confusing", label: "Confusing", icon: HelpCircle },
  { id: "praise", label: "Praise", icon: Heart },
];

// Hidden on embed widget + admin dashboard (admins have the review panel instead).
const HIDDEN_PREFIXES = ["/embed", "/admin"];

export default function FeedbackWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("idea");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;

  const reset = () => { setMessage(""); setEmail(""); setCategory("idea"); setDone(false); };

  const submit = async () => {
    if (message.trim().length < 3) { toast.error("Please add a little more detail."); return; }
    setBusy(true);
    try {
      await api.post("/feedback", {
        message: message.trim(),
        category,
        email: email.trim() || null,
        page_url: window.location.href,
      });
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="feedback-fab"
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-ff-blue px-4 py-3 text-sm font-bold text-white shadow-lg transition-transform duration-200 hover:-translate-y-0.5 hover:bg-ff-blue/90"
        aria-label="Send beta feedback"
      >
        <MessageSquarePlus className="h-4 w-4" />
        <span className="hidden sm:inline">Feedback</span>
      </button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setTimeout(reset, 200); }}>
        <DialogContent className="max-w-md" data-testid="feedback-dialog">
          {done ? (
            <div className="py-6 text-center" data-testid="feedback-success">
              <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-ff-green" />
              <DialogHeader>
                <DialogTitle className="text-center font-heading text-xl font-extrabold text-ff-blue">Thank you!</DialogTitle>
                <DialogDescription className="text-center">
                  Your feedback went straight to the FightFund team. It really helps us during beta.
                </DialogDescription>
              </DialogHeader>
              <Button onClick={() => setOpen(false)} data-testid="feedback-close" className="mt-5 bg-ff-blue text-white hover:bg-ff-blue/90">Done</Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-heading text-lg font-extrabold text-ff-blue">
                  <MessageSquarePlus className="h-5 w-5" /> Send feedback
                </DialogTitle>
                <DialogDescription>
                  You're helping test FightFund. Tell us what's working, what's broken, or what you'd change.
                </DialogDescription>
              </DialogHeader>

              <div className="mt-2 space-y-4">
                <div>
                  <Label className="text-ff-blue">What kind of feedback?</Label>
                  <div className="mt-2 grid grid-cols-4 gap-2">
                    {CATEGORIES.map((c) => (
                      <button key={c.id} type="button" data-testid={`feedback-cat-${c.id}`} onClick={() => setCategory(c.id)}
                        className={`flex flex-col items-center gap-1 rounded-lg border py-2.5 text-xs font-bold transition-colors duration-200 ${category === c.id ? "border-ff-blue bg-ff-blue text-white" : "border-ff-border text-ff-blue hover:border-ff-blue"}`}>
                        <c.icon className="h-4 w-4" /> {c.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="fb-msg" className="text-ff-blue">Your feedback</Label>
                  <Textarea id="fb-msg" data-testid="feedback-message" rows={4} value={message}
                    onChange={(e) => setMessage(e.target.value)} className="mt-1.5"
                    placeholder="What happened, or what would make this better?" />
                </div>

                <div>
                  <Label htmlFor="fb-email" className="text-ff-blue">Email <span className="font-normal text-ff-medium">(optional — if you'd like a reply)</span></Label>
                  <Input id="fb-email" type="email" data-testid="feedback-email" value={email}
                    onChange={(e) => setEmail(e.target.value)} className="mt-1.5" placeholder="you@example.com" />
                </div>

                <Button onClick={submit} disabled={busy} data-testid="feedback-submit"
                  className="w-full bg-ff-action font-semibold text-white hover:bg-ff-actionDark">
                  {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : <><Send className="mr-2 h-4 w-4" /> Send feedback</>}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
